import {
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ReorderableList,
	Select,
	SelectOption,
	TextInput,
	ValidationError
} from "client/Components";
import { IInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { ContentKind } from "client/Api/Workspace/LiveInformation/Email/emailLiveInformation";
import {
	UpdateEmailAppTabModel,
	UpdateOtherMailboxModel,
	UpdatePersonalMailboxModel,
	UpdateSharedMailboxModel,
	UpdateUserMailboxModel
} from "client/Api/Workspace/workspace";
import { FolderInput } from "./FolderInput";

const UserMailbox = "UserMailbox";
const PersonalMailbox = "PersonalMailbox";
const OtherMailbox = "OtherMailbox";

const contentOptions: readonly RadioButtonGroupItem<ContentKind>[] = [
	{
		text: localize("_OnlyShowUnreadEmailMessages"),
		value: ContentKind.Unread
	}, {
		text: localize("_ShowReadAndUnreadEmailMessages"),
		value: ContentKind.ReadAndUnread
	}
];

export type MailBoxKind = StringKeyOf<MailBoxModel>;

export type MailBoxModel = {
	user?: UpdateUserMailboxModel;
	personal?: UpdatePersonalMailboxModel;
	shared?: UpdateSharedMailboxModel;
	other?: UpdateOtherMailboxModel;
};

type AvailableMailboxOptionValue = {
	kind: MailBoxKind;
	value: string;
};

type EditEmailTabItemProps = {
	attachable: IValueInputAttachable<UpdateEmailAppTabModel>;
	sharedMailboxes: string[];
	personalMailbox: string | null;
	isAdmin: boolean;
	onRemoveTab: () => void;
};

export const EditEmailTabItem = (props: EditEmailTabItemProps): ReactElement => {
	const {
		attachable,
		sharedMailboxes,
		personalMailbox,
		isAdmin,
		onRemoveTab
	} = props;

	const nameAttachable: IInputAttachable<string> = attachable.for("name");

	const [availableMailboxesOptions, setAvailableMailboxesOptions] = useState<SelectOption<AvailableMailboxOptionValue>[]>([]);

	const attachableValue = useMemo(
		(): UpdateEmailAppTabModel => attachable.value,
		[attachable.value]
	);

	useEffect(
		() => {
			const options: SelectOption<AvailableMailboxOptionValue>[] = [];

			if (isAdmin) {
				options.push({
					text: localize("_UserMailbox"),
					value: {
						kind: "user",
						value: UserMailbox
					}
				});
			}

			options.push(
				{
					text: personalMailbox ?? localize("_YourMailbox"),
					value: {
						kind: "personal",
						value: PersonalMailbox,
					}
				},
				...sharedMailboxes.map((x: string): SelectOption<AvailableMailboxOptionValue> => ({
					text: x,
					value: {
						kind: "shared",
						value: x
					}
				})),
				{
					text: localize("_OtherMailbox"),
					value: {
						kind: "other",
						value: OtherMailbox
					}
				}
			);

			setAvailableMailboxesOptions(options);
		},
		[
			isAdmin,
			personalMailbox,
			sharedMailboxes
		]
	);

	const selectedEmailValue = useMemo(
		(): nil | AvailableMailboxOptionValue => {
			if (isNotNil(attachableValue.shared)) {
				return availableMailboxesOptions
					.find(x=> {
						return x.value
							.kind === "shared"
								&& x.value
									.value === attachableValue.shared
										?.mailbox
										.smtpAddress;
					})?.value;
			}

			if (isNotNil(attachableValue.personal)) {
				return availableMailboxesOptions
					.find(x=> {
						return x.value
							.kind === "personal";
					})?.value;
			}

			if (isNotNil(attachableValue.other)) {
				return availableMailboxesOptions
					.find(x=> {
						return x.value
							.kind === "other";
					})?.value;
			}

			if (isNotNil(attachableValue.user)) {
				return availableMailboxesOptions
					.find(x=> {
						return x.value
							.kind === "user";
					})?.value;
			}

			return null;
		},
		[
			attachableValue,
			availableMailboxesOptions
		]
	);

	const currentMailbox = useMemo(
		(): string => {
			if (selectedEmailValue?.value === OtherMailbox) {
				return attachableValue.other
					?.mailbox
					.smtpAddress ?? "";
			}
			if (selectedEmailValue?.value === PersonalMailbox) {
				return personalMailbox ?? "";
			}
			return selectedEmailValue?.value ?? "";
		},
		[
			selectedEmailValue,
			personalMailbox,
			attachableValue.other
				?.mailbox
				.smtpAddress
		]
	);

	const currentMailboxKind = useMemo(
		(): MailBoxKind	=> {
			const {
				personal,
				shared,
				other,
			} = attachableValue;

			if (personal) {
				return "personal";
			}
			if (shared) {
				return "shared";
			}
			if (other) {
				return "other";
			}

			return "user";
		},
		[attachableValue]
	);

	const emailChangeHandler = useCallback(
		(newValue: AvailableMailboxOptionValue): void => {
			const mailBoxModel: MailBoxModel = {
				user: undefined,
				personal: undefined,
				shared: undefined,
				other: undefined,
			};

			switch (newValue.kind) {
				case "other":
					mailBoxModel.other = {
						mailbox: {
							smtpAddress: ""
						},
						folder: null
					};
					break;

				case "user":
					mailBoxModel.user = {
						folder: null
					};
					break;

				case "personal":
					mailBoxModel.personal = {
						folder: null
					};
					break;
				case "shared":
					mailBoxModel.shared = {
						mailbox: {
							smtpAddress: newValue.value
						},
						folder: null
					};
					break;
				default:
					never(newValue.kind);
			}

			attachable.reducer
				.setValue({
					...attachableValue,
					...mailBoxModel
				});

			attachable.validation
				.triggerInputFinished();
		},
		[
			attachable,
			attachableValue
		]
	);

	return (
		<ReorderableList.PanelItem
			{...attachable.attachToReorderableListPanelItem(true)}
			header={
				<Select
					value={selectedEmailValue}
					onChange={emailChangeHandler}
					options={availableMailboxesOptions}
					placeholder={localize("_PleaseSelect")}
					trackingId={generateTrackingId(
						`${attachable.reducer
							.path}__select-email`
					)}
					menuTitle={localize("_Source")}
				/>
			}
			onRemove={onRemoveTab}
		>
			{
				attachable.for("other")
					.whenType(
						isNotNil,
						attachableWithSmtpAddress => (
							<FormItem label={localize("_Mailbox")}>
								<TextInput
									{...attachableWithSmtpAddress.for("mailbox")
										.for("smtpAddress")
										.attachToTextInput()
									}
									placeholder="example@example.com"
								/>
								<ValidationError
									{...attachableWithSmtpAddress.for("mailbox")
										.for("smtpAddress")
										.attachToValidationError()
									}
								/>
							</FormItem>
						)
					)
			}
			{attachable.for(currentMailboxKind)
				.whenType(
					isNotNil,
					(attachableWithFolder) => {
						return (
							<FolderInput
								attachable={attachableWithFolder.for("folder")}
								currentMailbox={currentMailbox}
								currentMailboxKind={currentMailboxKind}
							/>
						);
					}
				)
			}
			<FormItem
				label={localize("_Content")}
				isForGroup
			>
				<RadioButtonGroup
					{...attachable.for("contentKind")
						.attachToRadioButtonGroup()
					}
					options={contentOptions}
				/>
				<ValidationError
					{...attachable.for("contentKind")
						.attachToValidationError()
					}
				/>
			</FormItem>
			<FormItem label={localize("_TabName")}>
				<TextInput
					{...nameAttachable.attachToTextInput()}
				/>
				<ValidationError
					{...nameAttachable.attachToValidationError()}
				/>
			</FormItem>
		</ReorderableList.PanelItem>
	);
};

EditEmailTabItem.displayName = "EditEmailTabItem";